/**
 * FixRunHistoryPage - Phase 9: FixRun History & Operations
 * 
 * Audit-scoped FixRun history with filtering, search, and batch operations.
 * 
 * Features:
 * - Dense table/list with FixRun details
 * - Real search and status filters backed by API
 * - URL query state persistence
 * - Cursor-based pagination
 * - Batch selection and cancel for eligible runs
 * - Confirmation modal for batch cancel
 * - Partial success handling
 * - Deep links to Finding and Audit context
 * - Responsive design (1440x900, 1366x768, narrow)
 * - Accessible table with keyboard navigation
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, FixRunListResponse, FixRunStatus } from '../lib/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { EmptyState } from '../components/EmptyState'
import { StatusBadge } from '../components/StatusBadge'
import { useLocale } from '../contexts/LocaleContext'

const STATUS_OPTIONS: FixRunStatus[] = ['pending', 'running', 'completed', 'partial', 'failed', 'cancelled', 'timeout']

export function FixRunHistoryPage() {
  const { t } = useLocale()
  const navigate = useNavigate()
  const { auditId } = useParams<{ auditId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  
  const [loading, setLoading] = useState(!!auditId) // Only load if auditId exists
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<FixRunListResponse | null>(null)
  
  // Selection state for batch operations
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelInProgress, setCancelInProgress] = useState(false)
  const [cancelResult, setCancelResult] = useState<{ cancelled: string[]; skipped: any[]; errors: any[] } | null>(null)
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null)
  
  // Parse filters from URL
  const statusFilter = searchParams.get('statuses') || undefined
  const searchQuery = searchParams.get('search') || undefined
  const cursor = searchParams.get('cursor') || undefined
  const limit = parseInt(searchParams.get('limit') || '20')
  
  // Load FixRuns
  useEffect(() => {
    if (!auditId) return
    loadFixRuns()
  }, [auditId, searchParams])
  
  async function loadFixRuns() {
    if (!auditId) return
    
    try {
      setLoading(true)
      setError(null)
      const result = await api.listFixRuns(auditId, {
        statuses: statusFilter,
        search: searchQuery,
        limit,
        cursor,
      })
      setData(result)
      setSelectedIds(new Set()) // Clear selection on reload
    } catch (err) {
      setError(err instanceof Error ? err.message : t('fixRuns.failedLoad'))
    } finally {
      setLoading(false)
    }
  }
  
  function updateFilters(updates: Record<string, string | undefined>) {
    const newParams = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === '') {
        newParams.delete(key)
      } else {
        newParams.set(key, value)
      }
    })
    // Reset cursor when filters change
    if ('statuses' in updates || 'search' in updates) {
      newParams.delete('cursor')
    }
    setSearchParams(newParams)
  }
  
  function handleStatusFilterChange(status: string) {
    const current = statusFilter?.split(',').filter(Boolean) || []
    const updated = current.includes(status)
      ? current.filter(s => s !== status)
      : [...current, status]
    updateFilters({ statuses: updated.length > 0 ? updated.join(',') : undefined })
  }
  
  function handleSearchChange(search: string) {
    updateFilters({ search: search || undefined })
  }
  
  function handlePageChange(newCursor: string | null) {
    updateFilters({ cursor: newCursor || undefined })
  }
  
  // Batch selection
  function toggleSelection(id: string) {
    const newSet = new Set(selectedIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedIds(newSet)
  }
  
  function toggleSelectAll() {
    if (!data) return
    const cancellableIds = data.items.filter(item => item.cancellable).map(item => item.fix_run_id)
    if (selectedIds.size === cancellableIds.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(cancellableIds))
    }
  }
  
  async function handleBatchCancel() {
    if (!auditId || selectedIds.size === 0) return
    
    setCancelInProgress(true)
    try {
      const result = await api.batchCancelFixRuns({
        fix_run_ids: Array.from(selectedIds),
        audit_run_id: auditId,
      })
      setCancelResult(result)
      // Reload after cancel
      await loadFixRuns()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('fixRuns.batchFailed'))
    } finally {
      setCancelInProgress(false)
      setShowCancelConfirm(false)
    }
  }
  
  // Determine if all cancellable items are selected
  const allCancellableSelected = useMemo(() => {
    if (!data) return false
    const cancellable = data.items.filter(item => item.cancellable)
    return cancellable.length > 0 && cancellable.every(item => selectedIds.has(item.fix_run_id))
  }, [data, selectedIds])
  
  // Determine indeterminate state (some but not all selected)
  const selectAllIndeterminate = useMemo(() => {
    if (!data) return false
    const cancellable = data.items.filter(item => item.cancellable)
    const selectedCancellable = cancellable.filter(item => selectedIds.has(item.fix_run_id))
    return selectedCancellable.length > 0 && selectedCancellable.length < cancellable.length
  }, [data, selectedIds])
  
  // Set indeterminate property on select-all checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = selectAllIndeterminate
    }
  }, [selectAllIndeterminate])
  
  if (!auditId) {
    return (
      <div className="p-8">
        <EmptyState
          title={t('fixRuns.noAudit')}
          description={t('fixRuns.noAuditBody')}
        />
      </div>
    )
  }
  
  if (loading && !data) {
    return <LoadingSpinner />
  }
  
  if (error && !data) {
    return (
      <div className="p-8">
        <div className="bg-red-50 text-red-800 p-4 rounded-lg">
          <p className="font-semibold">{t('fixRuns.errorTitle')}</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={() => loadFixRuns()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }
  
  const hasData = data && data.items.length > 0
  const hasFilters = Boolean(statusFilter || searchQuery)
  
  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('fixRuns.title')}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('fixRuns.auditMeta', { id: auditId.slice(0, 8), n: data?.pagination.total || 0 })}
            </p>
          </div>
          <button
            onClick={() => navigate(`/audit/${auditId}`)}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
          >
            {t('fixRuns.backAudit')}
          </button>
        </div>
        
        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[240px]">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                {t('fixRuns.search')}
              </label>
              <input
                id="search"
                type="text"
                value={searchQuery || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('fixRuns.searchPh')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Status Filter */}
            <div className="flex-1 min-w-[240px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('fixRuns.status')}
              </label>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map(status => {
                  const isActive = statusFilter?.split(',').includes(status)
                  return (
                    <button
                      key={status}
                      onClick={() => handleStatusFilterChange(status)}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                        isActive
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {status}
                    </button>
                  )
                })}
              </div>
            </div>
            
            {/* Clear Filters */}
            {hasFilters && (
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchParams({})
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                >
                  {t('fixRuns.clearAll')}
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Batch Actions */}
        {selectedIds.size > 0 && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">
              {selectedIds.size === 1
                ? t('fixRuns.selectedOne', { n: selectedIds.size })
                : t('fixRuns.selectedMany', { n: selectedIds.size })}
            </span>
            <button
              onClick={() => setShowCancelConfirm(true)}
              disabled={cancelInProgress}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('fixRuns.cancelSelected')}
            </button>
          </div>
        )}
        
        {/* Cancel Result */}
        {cancelResult && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm font-medium text-green-900 mb-2">{t('fixRuns.batchComplete')}</p>
            <div className="text-sm text-green-800 space-y-1">
              <p>✓ {t('fixRuns.cancelled', { n: cancelResult.cancelled.length })}</p>
              {cancelResult.skipped.length > 0 && (
                <p>⊘ {t('fixRuns.skipped', { n: cancelResult.skipped.length, reasons: cancelResult.skipped.map(s => s.reason).join(', ') })}</p>
              )}
              {cancelResult.errors.length > 0 && (
                <p className="text-red-600">✗ {t('fixRuns.errors', { n: cancelResult.errors.length })}</p>
              )}
            </div>
            <button
              onClick={() => setCancelResult(null)}
              className="mt-2 text-xs text-green-700 hover:text-green-900 underline"
            >
              {t('fixRuns.dismiss')}
            </button>
          </div>
        )}
      </div>
      
      {/* Table */}
      {!hasData ? (
        <EmptyState
          title={hasFilters ? t('fixRuns.noneMatch') : t('fixRuns.noneYet')}
          description={hasFilters ? t('fixRuns.noneMatchBody') : t('fixRuns.noneYetBody')}
        />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" role="table" aria-label={t('fixRuns.tableAria')}>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allCancellableSelected}
                      onChange={toggleSelectAll}
                      aria-label={t('fixRuns.selectAllAria')}
                      className="rounded border-gray-300"
                      disabled={!data || data.items.filter(item => item.cancellable).length === 0}
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">{t('fixRuns.colId')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">{t('fixRuns.colStatus')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">{t('fixRuns.colBranch')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">{t('fixRuns.colBase')}</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">{t('fixRuns.colApplied')}</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">{t('fixRuns.colFailed')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">{t('fixRuns.colCreated')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">{t('fixRuns.colCompleted')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.items.map((item) => (
                  <tr
                    key={item.fix_run_id}
                    className={`hover:bg-gray-50 ${selectedIds.has(item.fix_run_id) ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      {item.cancellable ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.fix_run_id)}
                          onChange={() => toggleSelection(item.fix_run_id)}
                          aria-label={`Select fix run ${item.fix_run_id.slice(0, 8)}`}
                          className="rounded border-gray-300"
                        />
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={`/fix/${item.fix_run_id}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/fix/${item.fix_run_id}`) }}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-xs"
                      >
                        {item.fix_run_id.slice(0, 8)}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs max-w-[200px] truncate" title={item.target_branch}>
                      {item.target_branch}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs max-w-[120px] truncate" title={item.base_ref}>
                      {item.base_ref}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={item.fixes_applied > 0 ? 'text-green-600 font-medium' : 'text-gray-500'}>
                        {item.fixes_applied}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={item.fixes_failed > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                        {item.fixes_failed}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {new Date(item.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {item.completed_at ? new Date(item.completed_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {data.pagination.has_more && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {t('fixRuns.showing', { shown: data.items.length, total: data.pagination.total })}
              </div>
              <button
                onClick={() => handlePageChange(data.pagination.next_cursor)}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                {t('fixRuns.nextPage')}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
            <h2 id="cancel-title" className="text-lg font-bold text-gray-900 mb-2">
              {t('fixRuns.confirmTitle')}
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              {t('fixRuns.confirmLead')} <strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? t('fixRuns.confirmOne') : t('fixRuns.confirmMany')}.
              {' '}
              {t('fixRuns.confirmNote')}
            </p>
            <p className="text-xs text-gray-600 mb-6">
              {t('fixRuns.confirmHint')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelInProgress}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleBatchCancel}
                disabled={cancelInProgress}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelInProgress ? t('fixAi.cancelling') : t('fixRuns.confirmCancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
